"use client";

import { Fragment, useEffect, useMemo, useState } from "react";
import { Flame } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import type { LeaderboardData, LeaderboardEntry } from "@/lib/streak/types";
import { formatRank, topPercentLabel } from "@/lib/streak/milestones";
import {
  relativeBuildDay,
  splitLeaderboard,
  TIE_BREAK_EXPLAINER,
  unrankedHeadline,
} from "@/lib/streak/leaderboard";

interface LeaderboardModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Deterministic 1–2 char initials for a display name / handle. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Stable avatar tint from the name so rows are visually distinct but calm. */
const AVATAR_TINTS = [
  "bg-orange-500/15 text-orange-600 dark:text-orange-400",
  "bg-sky-500/15 text-sky-600 dark:text-sky-400",
  "bg-violet-500/15 text-violet-600 dark:text-violet-400",
  "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  "bg-rose-500/15 text-rose-600 dark:text-rose-400",
  "bg-amber-500/15 text-amber-600 dark:text-amber-400",
];
function tintFor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return AVATAR_TINTS[Math.abs(h) % AVATAR_TINTS.length];
}

// Podium chips for the top three. Every rank shows its NUMBER — the medal only
// tints it. Ranks are unique now (migration 164), so the number a row carries is
// exactly its position in the list, and hiding it behind an icon would throw
// away the one thing the user came here to read.
const MEDAL: Record<number, string> = {
  1: "bg-gradient-to-b from-amber-300 to-amber-500 text-white ring-1 ring-amber-400/40",
  2: "bg-gradient-to-b from-zinc-200 to-zinc-400 text-white ring-1 ring-zinc-300/40",
  3: "bg-gradient-to-b from-orange-300 to-orange-600 text-white ring-1 ring-orange-400/40",
};

function RankChip({ rank }: { rank: number }) {
  const ranked = rank > 0;
  return (
    <span
      className={cn(
        "flex h-7 w-9 shrink-0 items-center justify-center rounded-lg text-[13px] font-semibold tabular-nums",
        ranked
          ? (MEDAL[rank] ?? "bg-muted text-muted-foreground")
          : "bg-muted/60 text-muted-foreground/60",
      )}
      aria-hidden
    >
      {ranked ? rank : "—"}
    </span>
  );
}

function Row({ entry }: { entry: LeaderboardEntry }) {
  const built = relativeBuildDay(entry.lastActiveDate);
  return (
    <li
      className={cn(
        "flex items-center gap-3 rounded-xl px-2.5 py-2 transition-colors",
        entry.isCurrentUser
          ? "bg-orange-500/10 ring-1 ring-orange-500/30"
          : "hover:bg-muted/60",
      )}
    >
      <RankChip rank={entry.ranked ? entry.rank : 0} />
      <div
        className={cn(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold",
          tintFor(entry.name),
        )}
        aria-hidden
      >
        {initials(entry.name)}
      </div>
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1.5 truncate text-sm font-medium text-foreground">
          <span className="truncate">
            {entry.ranked && (
              <span className="sr-only">Rank {entry.rank}: </span>
            )}
            {entry.name}
          </span>
          {entry.isCurrentUser && (
            <span className="shrink-0 rounded-full bg-orange-500 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
              You
            </span>
          )}
        </p>
        <p className="truncate text-xs text-muted-foreground">
          Best {entry.longestStreak}d · {entry.totalActiveDays} days built
          {/* The recency tie-breaker, made visible — but only where there's room
              for it, so the line never truncates the numbers on a phone. */}
          {built && <span className="hidden sm:inline"> · built {built}</span>}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1 text-sm font-semibold tabular-nums text-foreground">
        <Flame
          className={cn(
            "h-4 w-4",
            entry.currentStreak > 0
              ? "fill-orange-500 text-orange-500"
              : "text-muted-foreground/50",
          )}
          aria-hidden
        />
        <span>
          {entry.currentStreak}
          <span className="sr-only">
            {" "}
            day streak{entry.currentStreak === 1 ? "" : "s"}
          </span>
        </span>
      </div>
    </li>
  );
}

function SkeletonRows() {
  return (
    <div className="flex flex-col gap-1.5">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-2.5 py-2">
          <div className="h-7 w-9 shrink-0 animate-pulse rounded-lg bg-muted" />
          <div className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-muted" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 w-1/3 animate-pulse rounded bg-muted" />
            <div className="h-2.5 w-1/4 animate-pulse rounded bg-muted" />
          </div>
          <div className="h-4 w-8 animate-pulse rounded bg-muted" />
        </div>
      ))}
    </div>
  );
}

export function LeaderboardModal({ open, onOpenChange }: LeaderboardModalProps) {
  const [data, setData] = useState<LeaderboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    // Wrapped in an async fn so the initial loading/errored reset runs inside a
    // function call, not synchronously in the effect body (react-hooks rule).
    const run = async () => {
      setLoading(true);
      setErrored(false);
      try {
        const r = await fetch("/api/leaderboard", { cache: "no-store" });
        if (!r.ok) throw new Error(`leaderboard ${r.status}`);
        const d = (await r.json()) as LeaderboardData;
        if (alive) setData(d);
      } catch {
        if (alive) setErrored(true);
      } finally {
        if (alive) setLoading(false);
      }
    };
    void run();
    return () => {
      alive = false;
    };
  }, [open]);

  const entries = useMemo(() => data?.entries ?? [], [data]);
  const { ranked, resting } = useMemo(
    () => splitLeaderboard(entries),
    [entries],
  );

  const isRanked = !!data && data.userRank > 0;
  const percentLabel = data ? topPercentLabel(data.percentile, data.userRank) : null;
  // The caller's own row carries their lifetime days; absent means never built.
  const myTotalActiveDays =
    entries.find((e) => e.isCurrentUser)?.totalActiveDays ?? 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flame className="h-4 w-4 fill-orange-500 text-orange-500" aria-hidden />
            Builder leaderboard
          </DialogTitle>
          <DialogDescription>
            {!data ? (
              "Ranked by current building streak."
            ) : isRanked ? (
              <>
                You&apos;re{" "}
                <span className="font-semibold text-foreground">
                  {formatRank(data.userRank, data.totalRanked)}
                </span>
                {percentLabel ? ` — ${percentLabel}` : ""}.
              </>
            ) : (
              unrankedHeadline({
                totalRanked: data.totalRanked,
                totalActiveDays: myTotalActiveDays,
              })
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[55vh] overflow-y-auto overscroll-contain pr-0.5">
          {loading ? (
            <SkeletonRows />
          ) : errored ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Couldn&apos;t load the leaderboard. Try again in a moment.
            </p>
          ) : ranked.length === 0 && resting.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No builders on a streak yet. Be the first — build something today.
            </p>
          ) : (
            <>
              <ol className="flex flex-col gap-1.5">
                {ranked.map((entry) => (
                  <Fragment key={`r-${entry.rank}`}>
                    {/* Break ONLY before the caller's own row when it sits below
                        the shown top N. Ranks are contiguous otherwise, so a gap
                        here always means "rows omitted" — never a tie. */}
                    {entry.appended && (
                      <li
                        className="py-1 text-center text-xs text-muted-foreground/60"
                        aria-hidden
                      >
                        ···
                      </li>
                    )}
                    <Row entry={entry} />
                  </Fragment>
                ))}
              </ol>

              {resting.length > 0 && (
                <>
                  {/* Builders with no live streak aren't ranked — showing them
                      inside the numbered list is what made the old board read as
                      broken. They sit below it, labelled, with a way back. */}
                  <div className="mt-3 flex items-center gap-2 px-2.5">
                    <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      Not on the board
                    </span>
                    <span className="h-px flex-1 bg-border" />
                  </div>
                  <ul className="mt-1.5 flex flex-col gap-1.5">
                    {resting.map((entry) => (
                      <Row key={`x-${entry.name}`} entry={entry} />
                    ))}
                  </ul>
                  <p className="mt-1.5 px-2.5 text-xs text-muted-foreground">
                    Only live streaks are ranked — a build today puts this row
                    back on the board.
                  </p>
                </>
              )}
            </>
          )}
        </div>

        {/* How the order was decided — the question a tied-looking board always
            raises. Kept out of the header so the rank stays the first thing read. */}
        {!loading && !errored && (ranked.length > 0 || resting.length > 0) && (
          <p className="border-t border-border pt-3 text-xs leading-relaxed text-muted-foreground">
            {TIE_BREAK_EXPLAINER}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
